/**
 * MarketplaceDetailModal — modeled on the GPT marketplace detail modal.
 *
 * Layout (top-down):
 *   - Big icon
 *   - Name + author + (optional) official badge
 *   - Tagline / description
 *   - Stats row: rating + install count
 *   - Capabilities (checkmarked bullet list)
 *   - Conversation Starters (chips)
 *   - Ratings histogram (5-star bars)
 *   - "More by <author>" — optional, only when present (we don't fetch
 *     these here in the first cut to keep the modal a single round-trip;
 *     can be added behind a useEffect later)
 *   - Fixed bottom CTA "Deploy to <projectName>"
 *
 * Accessibility:
 *   - role=dialog + aria-modal=true
 *   - focus trapped between the close button and the deploy button
 *   - Esc closes
 *   - Background click closes (with stopPropagation on the modal body)
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Marketplace from '../../services/marketplace';
import api from '../../services/api';
import { validateWorkflowName, suggestAvailableName } from '../../utils/workflow-name';
import { IonIcon } from '@ionic/react';
import {
  closeOutline,
  checkmarkCircle,
  alertCircleOutline,
  star,
  starOutline,
  downloadOutline,
  cloudDownloadOutline,
  chevronDownOutline,
  flashOutline,
} from 'ionicons/icons';
import './MarketplaceDetailModal.css';

const MarketplaceDetailModal = ({
  entry,
  projectId,
  projectName,
  isOpen,
  onClose,
  onDeploy,
  deploying,
  // Click handler invoked when the user picks a star count, signature:
  //   onRate(entry, n) — n is 1..5. Should POST the rating and resolve
  //   to the refreshed entry so the modal can update its histogram.
  //   When omitted, the stars stay non-interactive (display-only).
  onRate,
}) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Integration status — fetched lazily on open so we only spend the
  // round-trip when the modal is actually visible. null = not loaded
  // yet; { required:[], ... } = loaded; error = string when fetch failed.
  // Loading flag drives the skeleton.
  const [intStatus, setIntStatus] = useState(null);
  const [intLoading, setIntLoading] = useState(false);
  const [intError, setIntError] = useState(null);

  useEffect(() => {
    if (!isOpen || !entry?.slug || !projectId) {
      setIntStatus(null);
      setIntError(null);
      return undefined;
    }
    let cancelled = false;
    setIntLoading(true);
    setIntError(null);
    Marketplace.getIntegrationStatus(entry.slug, projectId)
      .then((res) => {
        if (cancelled) return;
        setIntStatus(res || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Graceful degrade — modal stays usable, deploy stays clickable.
        // The checklist section just won't render.
        setIntError(err?.message || 'Failed to load integration status');
      })
      .finally(() => {
        if (!cancelled) setIntLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, entry?.slug, projectId]);

  // ── Esc to close + focus trap ────────────────────────────────────
  // Lightweight focus trap: Tab/Shift-Tab cycles between the first
  // and last focusable element inside the dialog. Avoids pulling in a
  // dedicated trap-focus library; the focusable set inside this modal
  // is small enough (close button, chips for keyboard activation,
  // deploy button) that this hand-rolled cycle is fine.
  useEffect(() => {
    if (!isOpen) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;

    const previouslyFocused = document.activeElement;
    // Defer focus until paint so the open animation doesn't yank
    // attention from the user's last action.
    const t = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  // ── Workflow name field ─────────────────────────────────────────
  // Default to the template's slug; auto-suffix on collision with this
  // project's existing workflow names. Validation runs synchronously
  // against the locally-cached list of existing workflowTypes (fetched
  // once on open via GET /projects/{id}/workflows). The marketplace
  // deploy endpoint runs the same uniqueness check server-side and
  // 409s with a suggested alternative if a teammate happens to deploy
  // the same name during the gap.
  const [name, setName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [existingNames, setExistingNames] = useState([]);

  // Fetch the project's existing workflow names on open so we can
  // a) seed a non-colliding default, b) give instant offline validation
  // feedback while the server check is in flight.
  useEffect(() => {
    if (!isOpen || !projectId) return undefined;
    let cancelled = false;
    api.get(`/projects/${projectId}/workflows`)
      .then((res) => {
        if (cancelled) return;
        const names = Array.isArray(res)
          ? res.map((w) => w.workflowType).filter(Boolean)
          : [];
        setExistingNames(names);
      })
      .catch(() => {
        // Best-effort — if listing fails, we fall back to server-only
        // check on submit. Local instant-validation degrades to shape-only.
        if (!cancelled) setExistingNames([]);
      });
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  // Seed the input when the modal opens / entry changes. Picks the
  // template's defaultSlug (entry.slug) and auto-suffixes if taken.
  // Don't clobber a value the user has already started typing.
  useEffect(() => {
    if (!isOpen || !entry?.slug) return;
    if (nameDirty) return;
    const base = entry.slug;
    const seeded = suggestAvailableName(base, existingNames) || base;
    setName(seeded);
  }, [isOpen, entry?.slug, existingNames, nameDirty]);

  // Reset dirty flag when the modal closes — next open should re-seed
  // from the (possibly newly-deployed) project's workflow list.
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setNameDirty(false);
    }
  }, [isOpen]);

  // Validation state — combined shape + uniqueness check against the
  // project's existing workflow list (fetched once on open). The
  // marketplace deploy endpoint runs the same check server-side and
  // 409s on collision, so race conditions between this keystroke check
  // and another user deploying the same name at the same time get a
  // clean server error with a suggested alternative.
  const validation = useMemo(() => {
    return validateWorkflowName(name, existingNames);
  }, [name, existingNames]);

  const handleDeploy = useCallback((opts) => {
    if (deploying || !entry) return;
    if (!validation.valid) return;
    onDeploy?.(entry, { ...(opts || {}), workflowName: name });
  }, [deploying, entry, onDeploy, name, validation]);

  // Warm-pool dropdown — toggled via the chevron next to the main CTA.
  // Closes when the user picks a count, clicks outside, or hits Esc.
  const [warmMenuOpen, setWarmMenuOpen] = useState(false);
  const warmMenuRef = useRef(null);
  useEffect(() => {
    if (!warmMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (warmMenuRef.current && !warmMenuRef.current.contains(e.target)) {
        setWarmMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setWarmMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [warmMenuOpen]);

  // Rating state — derived from the entry but ephemeral on hover so the
  // user can preview the star count before committing. `submittingRating`
  // disables clicks during the inflight POST.
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  if (!isOpen || !entry) return null;

  const histogram = entry.ratingHistogram || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const histogramTotal = [1, 2, 3, 4, 5].reduce((a, k) => a + (histogram[k] || 0), 0);
  const rating = Number(entry.ratingAvg) || 0;
  const installCount = Number(entry.installCount) || 0;
  const myRatingValue = Number(entry.myRating?.rating) || 0;
  // Stars filled = hover (live preview) > my prior rating > rounded avg.
  // Showing "my rating" by default makes the affordance obvious (the
  // stars look "set") and matches App Store / GitHub patterns.
  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
  const canRate = typeof onRate === 'function';

  const deployLabel = projectName
    ? `Deploy to ${projectName}`
    : 'Deploy';

  return (
    <div className="mkt-modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="mkt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mkt-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            {entry.icon ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={entry.icon} alt="" className="mkt-modal-icon" />
            ) : (
              <span className="mkt-modal-icon mkt-modal-icon--fallback">
                {(entry.name || entry.slug || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <h2 id="mkt-modal-title" className="mkt-modal-title">{entry.name || entry.slug}</h2>
            <p className="mkt-modal-author">
              By {entry.authorName || 'Unknown'}
              {entry.authorType === 'official' && (
                <span className="mkt-modal-author-badge">Official</span>
              )}
            </p>
            <p className="mkt-modal-tagline">
              {entry.description || entry.tagline}
            </p>

            <div className="mkt-modal-stats" aria-label="Stats">
              <div className="mkt-modal-stat">
                <div className="mkt-modal-stat-value">
                  {rating.toFixed(1)}
                  {/* Stars: clickable when onRate is wired. The POST is
                      upsert by (workflowSlug, userId) on the backend, so
                      re-rating just replaces the prior row + recomputes
                      the histogram atomically. */}
                  <span
                    className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                    role={canRate ? 'radiogroup' : undefined}
                    aria-label={canRate ? 'Rate this workflow' : undefined}
                    onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                  >
                    {[1, 2, 3, 4, 5].map((n) => {
                      const filled = n <= starsFilled;
                      const StarButton = canRate ? 'button' : 'span';
                      const extraProps = canRate
                        ? {
                            type: 'button',
                            role: 'radio',
                            'aria-checked': n === myRatingValue,
                            'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                            disabled: submittingRating,
                            onMouseEnter: () => setHoverRating(n),
                            onFocus: () => setHoverRating(n),
                            onBlur: () => setHoverRating(0),
                            onClick: async () => {
                              if (!entry || submittingRating) return;
                              setSubmittingRating(true);
                              try {
                                await onRate(entry, n);
                              } finally {
                                setSubmittingRating(false);
                              }
                            },
                          }
                        : {};
                      return (
                        <StarButton
                          key={n}
                          className={`mkt-modal-star ${filled ? 'is-filled' : ''}`}
                          {...extraProps}
                        >
                          <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                        </StarButton>
                      );
                    })}
                  </span>
                </div>
                <div className="mkt-modal-stat-label">
                  {myRatingValue > 0
                    ? `You rated ${myRatingValue} · ${histogramTotal} ${histogramTotal === 1 ? 'rating' : 'ratings'}`
                    : `${histogramTotal} ${histogramTotal === 1 ? 'rating' : 'ratings'}`}
                </div>
              </div>
              <div className="mkt-modal-stat">
                <div className="mkt-modal-stat-value">
                  <IonIcon icon={cloudDownloadOutline} aria-hidden="true" />
                  {installCount.toLocaleString()}
                </div>
                <div className="mkt-modal-stat-label">deploys</div>
              </div>
              {entry.category && (
                <div className="mkt-modal-stat">
                  <div className="mkt-modal-stat-value mkt-modal-stat-category">
                    {entry.category}
                  </div>
                  <div className="mkt-modal-stat-label">category</div>
                </div>
              )}
            </div>
          </header>

          {Array.isArray(entry.conversationStarters) && entry.conversationStarters.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Conversation Starters</h3>
              <ul className="mkt-modal-chips">
                {entry.conversationStarters.slice(0, 6).map((cs, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="mkt-modal-chip"
                      // No-op click — these are illustrative, not action
                      // buttons. The keyboard-focusable button shape gives
                      // them tab order in the focus trap without inviting
                      // a click handler we don't have a destination for.
                      tabIndex={0}
                      onClick={() => {}}
                    >
                      {cs}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Integration checklist — only renders once we have data AND
              the listing actually requires one or more integrations. */}
          {intStatus && Array.isArray(intStatus.required) && intStatus.required.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Required integrations</h3>
              <ul className="mkt-modal-integrations">
                {intStatus.required.map((id) => {
                  const meta = intStatus.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
                  const isConnected = intStatus.connected?.includes(id);
                  return (
                    <li key={id} className={`mkt-modal-integration-row ${isConnected ? 'is-connected' : 'is-missing'}`}>
                      <span className="mkt-modal-integration-status">
                        <IonIcon icon={isConnected ? checkmarkCircle : alertCircleOutline} aria-hidden="true" />
                      </span>
                      <span className="mkt-modal-integration-name">{meta.name}</span>
                      <span className="mkt-modal-integration-state">
                        {isConnected
                          ? 'Connected'
                          : (
                            <a
                              href={meta.connectPath}
                              className="mkt-modal-integration-connect"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Connect after deploy
                            </a>
                          )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {intStatus.missing && intStatus.missing.length > 0 && (
                <p className="mkt-modal-integration-note">
                  Deploy is allowed; missing integrations can be connected later from Settings → Integrations.
                </p>
              )}
            </section>
          )}
          {intLoading && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Required integrations</h3>
              <p className="mkt-modal-integration-loading">Checking your connected integrations…</p>
            </section>
          )}
          {intError && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Required integrations</h3>
              <p className="mkt-modal-integration-error">Couldn’t load integration status. Deploy will still work; you may need to connect integrations from Settings after install.</p>
            </section>
          )}

          {Array.isArray(entry.capabilities) && entry.capabilities.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Capabilities</h3>
              <ul className="mkt-modal-capabilities">
                {entry.capabilities.map((cap, i) => (
                  <li key={i}>
                    <IonIcon icon={checkmarkCircle} aria-hidden="true" />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mkt-modal-section">
            <h3 className="mkt-modal-section-title">Ratings</h3>
            {histogramTotal === 0 ? (
              <p className="mkt-modal-no-ratings">No ratings yet.</p>
            ) : (
              <div className="mkt-modal-histogram" aria-label={`${histogramTotal} ratings`}>
                {[5, 4, 3, 2, 1].map((stars) => {
                  const count = histogram[stars] || 0;
                  const pct = histogramTotal > 0 ? (count / histogramTotal) * 100 : 0;
                  return (
                    <div key={stars} className="mkt-modal-histogram-row">
                      <span className="mkt-modal-histogram-label">
                        {stars}
                        <IonIcon icon={star} aria-hidden="true" />
                      </span>
                      <div className="mkt-modal-histogram-bar">
                        <div
                          className="mkt-modal-histogram-bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="mkt-modal-histogram-count">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <footer className="mkt-modal-footer">
          {/* Inline name field — split layout: the input flows into the
              left of the deploy split-button so the relationship between
              "what to call it" and "deploy" is unambiguous. Live shape
              validation (regex) is instant; uniqueness checks the server
              on each keystroke (debounced 300ms). */}
          {/* Label on the left, status/help on the right — same row */}
          <div className="mkt-modal-name-label-row">
            <label className="mkt-modal-name-label" htmlFor="mkt-modal-name-input">
              Workflow name
            </label>
            <p
              id="mkt-modal-name-help"
              className={`mkt-modal-name-help ${validation.valid ? 'is-valid' : (name ? 'is-invalid' : '')}`}
              aria-live="polite"
            >
              {!name
                ? ''
                : validation.valid
                  ? `Available — will deploy to ${projectName || 'this project'}`
                  : (
                    <>
                      {validation.reason || 'Pick a different name'}
                      {validation.suggestion && (
                        <>
                          {' — try '}
                          <button
                            type="button"
                            className="mkt-modal-name-suggestion"
                            onClick={() => { setName(validation.suggestion); setNameDirty(true); }}
                          >
                            {validation.suggestion}
                          </button>
                        </>
                      )}
                    </>
                  )}
            </p>
          </div>
          <div
            className={`mkt-modal-name-row ${validation.valid ? 'is-valid' : (name ? 'is-invalid' : '')}`}
            ref={warmMenuRef}
          >
            <div className="mkt-modal-name-input-wrap">
              <input
                id="mkt-modal-name-input"
                type="text"
                className="mkt-modal-name-input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameDirty(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && validation.valid && !deploying) {
                    e.preventDefault();
                    handleDeploy();
                  }
                }}
                placeholder={entry.slug || 'workflow-name'}
                aria-invalid={!validation.valid && !!name}
                aria-describedby="mkt-modal-name-help"
                autoComplete="off"
                spellCheck="false"
                disabled={deploying}
              />
              {name && (
                <span
                  className={`mkt-modal-name-status ${validation.valid ? 'is-valid' : 'is-invalid'}`}
                  aria-hidden="true"
                >
                  <IonIcon icon={validation.valid ? checkmarkCircle : alertCircleOutline} />
                </span>
              )}
            </div>
            {/* Wrap Deploy + chevron so the name-row's flex gap doesn't
                push them apart. The wrapper IS the visual pill; inner
                buttons are transparent click targets (see .css). */}
            <div className="mkt-modal-deploy-split">
              <button
                type="button"
                className="mkt-modal-deploy mkt-modal-deploy--main"
                onClick={() => handleDeploy()}
                disabled={deploying || !projectName || !validation.valid}
                aria-label={deployLabel}
                title={!validation.valid ? 'Pick a valid name' : undefined}
              >
                <IonIcon icon={downloadOutline} aria-hidden="true" />
                {deploying ? 'Deploying…' : 'Deploy'}
              </button>
              <button
                type="button"
                className="mkt-modal-deploy mkt-modal-deploy--chevron"
                onClick={() => setWarmMenuOpen((v) => !v)}
                disabled={deploying || !projectName || !validation.valid}
                aria-label="Deploy options"
                aria-haspopup="menu"
                aria-expanded={warmMenuOpen}
              >
                <IonIcon icon={chevronDownOutline} aria-hidden="true" />
              </button>
            </div>

            {warmMenuOpen && (
              <div className="mkt-modal-warm-menu" role="menu">
                <div className="mkt-modal-warm-menu-header">
                  <IonIcon icon={flashOutline} aria-hidden="true" />
                  <span>Warm pool</span>
                </div>
                <p className="mkt-modal-warm-menu-help">
                  Skip ~60s cold start by keeping Fargate tasks pre-warmed.
                  Paid feature — provisioning takes ~60s after deploy.
                </p>
                <ul className="mkt-modal-warm-menu-list">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <li key={n}>
                      <button
                        type="button"
                        role="menuitem"
                        className="mkt-modal-warm-menu-item"
                        onClick={() => {
                          setWarmMenuOpen(false);
                          handleDeploy({ warm: n });
                        }}
                        disabled={deploying || !projectName || !validation.valid}
                      >
                        <span className="mkt-modal-warm-menu-label">
                          Deploy with <span className="mkt-modal-warm-menu-count">{n}</span>{' '}
                          warm worker{n === 1 ? '' : 's'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MarketplaceDetailModal;
